import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

const path = (u) => { try { return new URL(u).pathname || "/"; } catch { return u || "/"; } };

// GSC verdict → бейдж індексу
function indexBadge(index) {
  if (!index || !index.verdict) return { c: "#9ca3af", label: "— не перевірено", title: "Натисніть «Перевірити індекс (Google)»" };
  const v = (index.verdict || "").toUpperCase();
  const cs = index.coverageState || "";
  const src = index.source === "serper" ? " · Google site:" : index.source === "gsc" ? " · GSC" : "";
  if (index.source === "serper") {
    return index.indexed === false || v === "FAIL"
      ? { c: "#dc2626", label: "🔴 Не в індексі", title: cs + src }
      : { c: "#16a34a", label: "🟢 В індексі", title: cs + src };
  }
  if (v === "PASS") return { c: "#16a34a", label: "🟢 В індексі", title: (cs || "Submitted and indexed") + src };
  if (v === "NEUTRAL") return { c: "#f59e0b", label: "🟡 Не в індексі", title: (cs || "Не проіндексовано") + src };
  if (v === "FAIL") return { c: "#dc2626", label: "🔴 Виключено", title: (cs || "Виключено з індексу") + src };
  return { c: "#f59e0b", label: cs ? `🟡 ${cs}` : "🟡 " + v, title: cs + src };
}

function httpBadge(code) {
  if (!code) return { c: "#9ca3af", label: "—" };
  if (code >= 200 && code < 300) return { c: "#16a34a", label: String(code) };
  if (code >= 300 && code < 400) return { c: "#f59e0b", label: String(code) };
  return { c: "#dc2626", label: String(code) };
}

function metaBadge(meta) {
  if (meta.published) return { c: "#16a34a", label: "✅ Опубліковано" };
  if (meta.status === "none") return { c: "#9ca3af", label: "— немає" };
  if (meta.source === "ai") return { c: "#3b82f6", label: "📝 Згенеровано (чернетка)" };
  return { c: "#a855f7", label: "✏️ Чернетка" };
}

export default function PagesOverviewView() {
  const { activeId, apiFetch, dark, t, card, inp, btnG, btnSm, flash, proj } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [openUrl, setOpenUrl] = useState(null);
  const [detail, setDetail] = useState({}); // url -> {loading, data}
  const [idxBusy, setIdxBusy] = useState(false);
  const [idxProg, setIdxProg] = useState(null); // {current,total,indexed}
  const [parseBusy, setParseBusy] = useState(false);
  const [parseProg, setParseProg] = useState(null); // {current,total}

  const load = async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/pages-overview`);
      const d = await r.json();
      if (r.ok) setData(d); else flash(d.error || "Помилка завантаження");
    } catch (e) { flash("Помилка: " + e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeId]);

  const toggle = async (url) => {
    if (openUrl === url) { setOpenUrl(null); return; }
    setOpenUrl(url);
    if (!detail[url]) {
      setDetail(prev => ({ ...prev, [url]: { loading: true } }));
      try {
        const r = await apiFetch(`/api/projects/${activeId}/pages-overview/detail?url=${encodeURIComponent(url)}`);
        const d = await r.json();
        setDetail(prev => ({ ...prev, [url]: { loading: false, data: r.ok ? d : null, error: r.ok ? null : d.error } }));
      } catch (e) {
        setDetail(prev => ({ ...prev, [url]: { loading: false, error: e.message } }));
      }
    }
  };

  // Спарсити ВСІ сторінки заново: тягне свіжий текст і зберігає (chunk по 20, щоб не впертись у таймаут).
  const parseAll = async () => {
    if (parseBusy) return;
    const urls = pages.map(p => p.url).filter(u => /^https?:\/\//.test(u));
    if (!urls.length) { flash("Немає сторінок для парсингу"); return; }
    if (!window.confirm(`Спарсити всі ${urls.length} сторінок заново? Тягне свіжий текст і зберігає — аналіз ключів піде на реальному контенті. Дані оновляться лише зараз.`)) return;
    setParseBusy(true); setParseProg({ current: 0, total: urls.length });
    const BATCH = 20; let done = 0, failed = 0;
    try {
      for (let i = 0; i < urls.length; i += BATCH) {
        const chunk = urls.slice(i, i + BATCH);
        const r = await apiFetch(`/api/projects/${activeId}/site-scan/fetch-bulk`, { method: "POST", body: JSON.stringify({ urls: chunk }) });
        const d = await r.json().catch(() => ({}));
        if (r.ok) { done += d.processed || 0; failed += d.failed || 0; }
        setParseProg({ current: Math.min(i + BATCH, urls.length), total: urls.length });
      }
      flash(`\u2713 Спарсено ${done} сторінок${failed ? `, помилок: ${failed}` : ""}`);
      setDetail({}); // скинути кеш розгортань — при відкритті підтягне свіже
      await load();
    } catch (e) { flash("Помилка: " + e.message); }
    setParseBusy(false); setParseProg(null);
  };

  const runIndexCheck = async () => {
    if (idxBusy) return;
    if (!window.confirm("Перевірити індексацію всіх сторінок через Google (site:)? Використовує Serper (без квоти GSC).")) return;
    setIdxBusy(true);
    setIdxProg({ current: 0, total: 0 });
    try {
      const r = await apiFetch(`/api/projects/${activeId}/pages-overview/index-check`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) { flash(d.error || "Помилка запуску"); setIdxBusy(false); setIdxProg(null); return; }
      setIdxProg({ current: 0, total: d.total });
      const poll = setInterval(async () => {
        try {
          const sR = await apiFetch(`/api/tasks/${d.taskId}`);
          if (!sR.ok) return;
          const s = await sR.json();
          setIdxProg({ current: s.progress?.current || 0, total: s.progress?.total || d.total, indexed: s.progress?.indexed });
          if (s.status === "completed" || s.status === "failed") {
            clearInterval(poll);
            setIdxBusy(false);
            setIdxProg(null);
            flash(s.status === "completed" ? `✓ Індексацію перевірено (в індексі: ${s.progress?.indexed ?? "?"})` : "Помилка перевірки індексації");
            await load();
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) { flash("Помилка: " + e.message); setIdxBusy(false); setIdxProg(null); }
  };

  const pages = data?.pages || [];
  const threshold = data?.threshold || 10;

  const filtered = useMemo(() => {
    let list = pages;
    if (filter === "published") list = list.filter(p => p.meta?.published);
    else if (filter === "no_meta") list = list.filter(p => (p.meta?.status || "none") === "none");
    else if (filter === "noindex") list = list.filter(p => p.index && (p.index.verdict || "").toUpperCase() !== "PASS");
    else if (filter === "http_err") list = list.filter(p => p.httpStatus && p.httpStatus >= 400);
    else if (filter === "top") list = list.filter(p => p.avgPos != null && p.avgPos <= threshold);
    else if (filter === "weak") list = list.filter(p => p.avgPos != null && p.avgPos > threshold);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter(p => (p.url || "").toLowerCase().includes(q) || (p.treeName || "").toLowerCase().includes(q));
    return list;
  }, [pages, filter, search, threshold]);

  const totals = useMemo(() => ({
    all: pages.length,
    published: pages.filter(p => p.meta?.published).length,
    noindex: pages.filter(p => p.index && (p.index.verdict || "").toUpperCase() !== "PASS").length,
    httpErr: pages.filter(p => p.httpStatus && p.httpStatus >= 400).length,
  }), [pages]);

  if (!proj) return null;

  const th = { padding: "8px 10px", fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "left", borderBottom: `1px solid ${t.border}`, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" };
  const td = { padding: "9px 10px", fontSize: 12.5, color: t.text, borderBottom: `1px solid ${t.border}`, verticalAlign: "middle" };

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: t.text }}>📊 Огляд сторінок</h1>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 4 }}>
            Статус мети, середня позиція, індекс, HTTP. Клік по рядку — ключі семантики з кількістю вхождень + LSI.
            {data?.avgSnapshotDate && <> · позиції від {new Date(data.avgSnapshotDate).toLocaleDateString("uk-UA")}</>}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={parseAll} disabled={parseBusy || loading}
            title="Спарсити всі сторінки заново: тягне свіжий текст живих сторінок і зберігає. Аналіз ключів піде на реальному контенті. Дані оновляться лише при натисканні."
            style={{ ...btnG, height: 32, cursor: (parseBusy || loading) ? "wait" : "pointer", opacity: parseBusy ? 0.7 : 1 }}>
            {parseBusy ? `🔄 ${parseProg?.current || 0}/${parseProg?.total || "…"}` : "🔄 Спарсити всі сторінки"}
          </button>
          <button onClick={runIndexCheck} disabled={idxBusy || loading}
            title="Перевірити індексацію через Google site: (Serper, без GSC-квоти)"
            style={{ ...btnG, height: 32, background: "#16a34a", color: "#fff", border: "none", cursor: (idxBusy || loading) ? "wait" : "pointer", opacity: idxBusy ? 0.7 : 1 }}>
            {idxBusy ? `🔎 ${idxProg?.current || 0}/${idxProg?.total || "…"}` : "🔎 Перевірити індекс (Google)"}
          </button>
          <button onClick={load} disabled={loading} style={{ ...btnG, height: 32 }}>↻ Оновити</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, margin: "14px 0" }}>
        <Stat label="Всього сторінок" value={totals.all} color="#6366f1" t={t} />
        <Stat label="Мета опубл." value={totals.published} color="#16a34a" t={t} />
        <Stat label="Не в індексі" value={totals.noindex} color="#f59e0b" t={t} />
        <Stat label="HTTP-помилки" value={totals.httpErr} color="#dc2626" t={t} />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 пошук URL…" style={{ ...inp, flex: 1, minWidth: 220, height: 32 }} />
        {[["all", "Усі"], ["published", "✅ Опубл."], ["no_meta", "Без мети"], ["top", `🟢 ТОП (≤${threshold})`], ["weak", `🔴 Поза ТОП`], ["noindex", "Не в індексі"], ["http_err", "HTTP ≥400"]].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} style={{ ...btnSm, background: filter === k ? "#6366f1" : "transparent", color: filter === k ? "#fff" : t.textMuted, border: `1px solid ${filter === k ? "#6366f1" : t.border}`, cursor: "pointer" }}>{l}</button>
        ))}
        <span style={{ fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>{loading ? "..." : `${filtered.length}/${pages.length}`}</span>
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...th, width: "36%" }}>Сторінка</th>
              <th style={{ ...th, width: "16%" }}>Мета</th>
              <th style={{ ...th, width: "12%" }}>Позиція</th>
              <th style={{ ...th, width: "18%" }}>Індекс</th>
              <th style={{ ...th, width: "8%" }}>HTTP</th>
              <th style={{ ...th, width: "10%" }}>Ключі СЯ</th>
            </tr></thead>
            <tbody>
              {filtered.map(p => {
                const mb = metaBadge(p.meta || {});
                const ib = indexBadge(p.index);
                const hb = httpBadge(p.httpStatus);
                const posColor = p.avgPos == null ? "#9ca3af" : p.avgPos <= threshold ? "#16a34a" : "#dc2626";
                const isOpen = openUrl === p.url;
                const det = detail[p.url];
                return (
                  <FragmentRow key={p.url}>
                    <tr onClick={() => toggle(p.url)} style={{ cursor: "pointer", background: isOpen ? (dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.05)") : "transparent" }}>
                      <td style={td}>
                        <span style={{ display: "inline-block", width: 14, color: t.textMuted, transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>▶</span>
                        <a href={p.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: t.text, textDecoration: "none", fontWeight: 600, fontSize: 12, wordBreak: "break-all" }}>{path(p.url)}</a>
                        {p.orphan && <span style={{ marginLeft: 6, fontSize: 9, color: "#06b6d4" }}>orphan</span>}
                        {p.cluster?.hfKeyword && <div style={{ fontSize: 10, color: t.textMuted, marginTop: 2, marginLeft: 14, fontStyle: "italic" }}>🔑 {p.cluster.hfKeyword}</div>}
                      </td>
                      <td style={td}><Badge c={mb.c}>{mb.label}</Badge></td>
                      <td style={td}>{p.avgPos == null ? <span style={{ color: "#9ca3af" }}>—</span> : <span style={{ fontWeight: 700, color: posColor }}>{p.avgPos <= threshold ? "🟢" : "🔴"} {p.avgPos}</span>}</td>
                      <td style={td}><span title={ib.title} style={{ color: ib.c, fontWeight: 600, fontSize: 11.5 }}>{ib.label}</span></td>
                      <td style={td}><span style={{ color: hb.c, fontWeight: 700 }}>{hb.label}</span></td>
                      <td style={td}>{p.cluster ? <span style={{ color: t.text }}>{p.cluster.keywordCount || 0}{p.keywordsFound != null && <span style={{ color: t.textMuted, fontSize: 10 }}> · знайд. {p.keywordsFound}</span>}</span> : <span style={{ color: t.textMuted }}>—</span>}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={6} style={{ ...td, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)", padding: 0 }}>
                          <ExpandDetail det={det} t={t} dark={dark} url={p.url} apiFetch={apiFetch} activeId={activeId} flash={flash} />
                        </td>
                      </tr>
                    )}
                  </FragmentRow>
                );
              })}
              {!loading && !filtered.length && <tr><td colSpan={6} style={{ ...td, textAlign: "center", padding: 28, color: t.textMuted }}>{pages.length ? "Нічого не знайдено" : "Спочатку завантажте структуру сайту"}</td></tr>}
              {loading && <tr><td colSpan={6} style={{ ...td, textAlign: "center", padding: 28, color: t.textMuted }}>Завантаження…</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function FragmentRow({ children }) { return <>{children}</>; }

function ExpandDetail({ det, t, dark, url, apiFetch, activeId, flash }) {
  const [bl, setBl] = useState(undefined);   // undefined = ще не чіпали; null = немає; {…} = дані
  const [blBusy, setBlBusy] = useState(false);
  // Ініціалізуємо беклінки з detail один раз.
  useEffect(() => { if (det?.data) setBl(det.data.backlinks || null); }, [det?.data]);

  if (!det || det.loading) return <div style={{ padding: 16, color: t.textMuted, fontSize: 12 }}>Завантаження…</div>;
  if (det.error || !det.data) return <div style={{ padding: 16, color: "#ef4444", fontSize: 12 }}>{det.error || "Немає даних"}</div>;
  const d = det.data;
  const onPageArr = Array.isArray(d.onPage) ? d.onPage : [];
  const notOnPageArr = Array.isArray(d.notOnPage) ? d.notOnPage : [];
  const totalKw = d.totals?.total || 0;
  const chip = (bg, color, brd) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 12, background: bg, color, fontSize: 11, fontWeight: 500, border: brd || "none" });
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("uk-UA") : "—";

  const checkBacklinks = async () => {
    if (blBusy) return;
    setBlBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/pages-overview/backlink-check`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url }),
      });
      const dd = await r.json();
      if (r.ok) { setBl({ current: dd.current, first: dd.first, firstAt: dd.firstAt, checkedAt: dd.checkedAt }); flash && flash(`✓ Беклінків зараз: ${dd.current}`); }
      else flash && flash(dd.error || "Помилка перевірки беклінків");
    } catch (e) { flash && flash("Помилка: " + e.message); }
    setBlBusy(false);
  };

  const pd = d.posDynamics;
  const delta = pd?.delta; // + = покращення
  return (
    <div style={{ padding: "14px 16px", display: "grid", gap: 14 }}>
      {/* ── Верхній ряд: позиція було→стало + беклінки ── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {/* Динаміка позиції */}
        <div style={{ flex: "1 1 280px", minWidth: 240, padding: "12px 14px", borderRadius: 10, background: dark ? "rgba(99,102,241,0.08)" : "rgba(99,102,241,0.05)", border: `1px solid ${t.border}` }}>
          <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>📈 Середня позиція сторінки</div>
          {pd && pd.firstAvg != null && pd.lastAvg != null ? (
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#9ca3af" }}>{pd.firstAvg}</div>
                <div style={{ fontSize: 9.5, color: t.textMuted }}>було · {fmtDate(pd.firstDate)}</div>
              </div>
              <div style={{ fontSize: 22, color: delta > 0 ? "#16a34a" : delta < 0 ? "#dc2626" : t.textMuted, fontWeight: 800 }}>
                {delta > 0 ? "↑" : delta < 0 ? "↓" : "→"}
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 30, fontWeight: 900, color: pd.lastAvg <= 10 ? "#16a34a" : "#dc2626" }}>{pd.lastAvg}</div>
                <div style={{ fontSize: 9.5, color: t.textMuted }}>стало · {fmtDate(pd.lastDate)}</div>
              </div>
              {delta != null && delta !== 0 && (
                <div style={{ fontSize: 12, fontWeight: 700, color: delta > 0 ? "#16a34a" : "#dc2626" }}>
                  {delta > 0 ? `+${delta} вгору` : `${delta} вниз`}
                </div>
              )}
            </div>
          ) : <div style={{ fontSize: 12, color: t.textMuted }}>Немає даних позицій (зніміть позиції).</div>}
        </div>

        {/* Беклінки */}
        <div style={{ flex: "1 1 280px", minWidth: 240, padding: "12px 14px", borderRadius: 10, background: dark ? "rgba(245,158,11,0.07)" : "rgba(245,158,11,0.05)", border: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
            <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>🔗 Беклінки на сторінку</span>
            <button onClick={checkBacklinks} disabled={blBusy}
              style={{ fontSize: 10.5, fontWeight: 600, padding: "4px 10px", borderRadius: 6, border: "none", cursor: blBusy ? "wait" : "pointer", background: "#f59e0b", color: "#fff", opacity: blBusy ? 0.7 : 1 }}>
              {blBusy ? "Перевіряю…" : "↻ Перевірити"}
            </button>
          </div>
          {bl ? (
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {bl.first != null && <div style={{ textAlign: "center" }}><div style={{ fontSize: 20, fontWeight: 800, color: "#9ca3af" }}>{bl.first}</div><div style={{ fontSize: 9.5, color: t.textMuted }}>на старті · {fmtDate(bl.firstAt)}</div></div>}
              {bl.first != null && <div style={{ fontSize: 18, color: t.textMuted }}>→</div>}
              <div style={{ textAlign: "center" }}><div style={{ fontSize: 26, fontWeight: 900, color: "#f59e0b" }}>{bl.current ?? "—"}</div><div style={{ fontSize: 9.5, color: t.textMuted }}>зараз</div></div>
              {bl.checkedAt && <span style={{ fontSize: 9.5, color: t.textMuted, marginLeft: "auto" }}>перевірено {fmtDate(bl.checkedAt)}</span>}
            </div>
          ) : <div style={{ fontSize: 12, color: t.textMuted }}>Ще не перевіряли. Натисніть «↻ Перевірити» (OpenSEO).</div>}
        </div>
      </div>

      {/* ── Конкуренти ── */}
      {d.competitors?.length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>🏆 Конкуренти цієї сторінки у видачі</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {d.competitors.map((c, i) => (
              <a key={i} href={`https://${c.domain}`} target="_blank" rel="noopener noreferrer"
                title={`зустрівся по ${c.count} ключах · сер. позиція ${c.avgPos}`}
                style={{ ...chip("rgba(245,158,11,0.12)", "#b45309", "1px solid rgba(245,158,11,0.3)"), textDecoration: "none" }}>
                {c.domain} <span style={{ opacity: 0.7, fontSize: 10 }}>· {c.count}кл · поз.{c.avgPos}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ── Ключі НА СТОРІНЦІ (у тілі) ── */}
      {totalKw > 0 ? (
        <>
          {d.textSource === "none" && <div style={{ fontSize: 11.5, color: "#f59e0b" }}>⚠️ Текст сторінки не отримано — лише H1/заголовки. Натисніть «🔄 Спарсити всі сторінки» вгорі.</div>}
          {d.textSource === "live" && <div style={{ fontSize: 11.5, color: t.textMuted }}>ℹ️ Текст із живої сторінки (не в кеші). «🔄 Спарсити всі сторінки» вгорі — щоб зберегти.</div>}
          <div>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>
              ✅ Ключі, що є в тексті сторінки ({onPageArr.length}/{totalKw}) — ×N = точна фраза, «слова є» = всі слова присутні (інший порядок/синонім)
            </div>
            {onPageArr.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {onPageArr.map((k, i) => {
                  const isWords = k.matchType === "words" || (k.count === 0);
                  return (
                    <span key={i} title={isWords ? `усі слова є на сторінці (не суцільною фразою) · частотність: ${k.frequency}${k.position != null ? ` · позиція: ${k.position}` : ""}` : `частотність у семантиці: ${k.frequency}${k.position != null ? ` · позиція: ${k.position}` : ""}`}
                      style={isWords ? chip("rgba(245,158,11,0.14)", "#b45309", "1px solid rgba(245,158,11,0.4)") : chip("rgba(16,185,129,0.16)", "#10b981", "1px solid rgba(16,185,129,0.4)")}>
                      {k.keyword}{isWords
                        ? <strong style={{ background: "#f59e0b", color: "#fff", borderRadius: 8, padding: "0 6px", fontSize: 9.5 }}>слова є</strong>
                        : <strong style={{ background: "#10b981", color: "#fff", borderRadius: 8, padding: "0 6px", fontSize: 10 }}>×{k.count}</strong>}
                      {k.position != null && <PosBadge pos={k.position} />}
                    </span>
                  );
                })}
              </div>
            ) : <div style={{ fontSize: 11.5, color: t.textMuted }}>Жоден ключ семантики не зустрічається в тексті сторінки.</div>}
          </div>

          <div>
            <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", marginBottom: 6 }}>
              ❌ Ключів НЕМАЄ в тексті сторінки ({notOnPageArr.length}/{totalKw}) — варто дописати в контент
            </div>
            {notOnPageArr.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {notOnPageArr.slice(0, 200).map((k, i) => (
                  <span key={i} title={`частотність у семантиці: ${k.frequency}${k.position != null ? ` · позиція: ${k.position}` : ""}`} style={chip("rgba(239,68,68,0.10)", "#ef4444", "1px solid rgba(239,68,68,0.25)")}>
                    {k.keyword}{k.frequency ? <span style={{ opacity: 0.6, fontSize: 10 }}>·{k.frequency}</span> : null}{k.position != null && <PosBadge pos={k.position} />}
                  </span>
                ))}
                {notOnPageArr.length > 200 && <span style={{ fontSize: 11, color: t.textMuted, padding: "3px 6px" }}>+{notOnPageArr.length - 200} ще</span>}
              </div>
            ) : <div style={{ fontSize: 11.5, color: "#10b981" }}>Усі ключі семантики вже є в тексті 🎉</div>}
          </div>
        </>
      ) : <div style={{ fontSize: 12, color: t.textMuted }}>Для цієї сторінки немає семантичного ядра.</div>}
    </div>
  );
}

// Бейдж позиції ключа (з модуля зняття позицій): колір за кошиком ТОП-3/10/30/100.
function PosBadge({ pos }) {
  const c = pos <= 3 ? "#059669" : pos <= 10 ? "#2563eb" : pos <= 30 ? "#7c3aed" : pos <= 100 ? "#b45309" : "#6b7280";
  return <span title={`позиція в Google: ${pos}`} style={{ background: c, color: "#fff", borderRadius: 8, padding: "0 6px", fontSize: 10, fontWeight: 700, marginLeft: 4 }}>#{pos}</span>;
}

function Badge({ children, c }) {
  return <span style={{ display: "inline-block", padding: "2px 7px", borderRadius: 5, background: `${c}1f`, color: c, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>{children}</span>;
}

function Stat({ label, value, color, t }) {
  return (
    <div style={{ padding: "10px 12px", background: `${color}12`, border: `1px solid ${color}33`, borderRadius: 10 }}>
      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "monospace", marginTop: 2 }}>{value == null ? "—" : value}</div>
    </div>
  );
}
